import { computed } from 'vue';
import { omit } from 'lodash';

import { useAddress } from '@/use/address';
import { RideTypeKey, TaxiRideTypeKey } from '@/models/rideType';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import type { CustomLocationType } from '@/models/address';
import type { ScheduledRideInput, StawavScheduledRideInput } from '@/models/scheduledRide';
import { RideServiceType } from '@/models/ride';
import { useRideTypes } from './rideTypes';

export const useScheduledRide = (data: any) => {
  const { trimAddress, getStreetAddressByType } = useAddress();
  const { findLocalTaxiRideType } = useRideTypes();

  const isFresnoEOCTaxi = computed(() => {
    return data.value.rideType === TaxiRideTypeKey.fresnoEOC;
  });

  const isGreenPointTaxi = computed(() => {
    return data.value.rideType === TaxiRideTypeKey.greenPoint;
  });

  const isLocalTaxiRideType = computed(() => {
    const localTaxi = findLocalTaxiRideType(data.value.rideType);
    return !!localTaxi;
  });

  const isStawavRideType = computed(() => {
    return data.value.rideType === RideTypeKey.staWav;
  });

  const isGeneralRideType = computed(() => {
    return !isLocalTaxiRideType.value && !isStawavRideType.value;
  });

  const isTransportation = computed(() => {
    return data.value.serviceType === RideServiceType.Transportation;
  });

  const isNeedToGetEstimation = computed(
    () => isGeneralRideType.value || isStawavRideType.value || isFresnoEOCTaxi.value || isGreenPointTaxi.value,
  );

  const isPharmacyOrder = computed(() => data.value.serviceType === RideServiceType.PharmacyDelivery);
  const isSpecialRequestOrder = computed(() => data.value.serviceType === RideServiceType.SpecialRequest);

  const isRepeatedRide = computed(() => data.value?.rideMultiple);

  const estimationBtnText = computed(() => {
    if (isNeedToGetEstimation.value) {
      return 'Get Estimate';
    }

    const localTaxi = findLocalTaxiRideType(data.value.rideType);

    if (!localTaxi) {
      return 'Get Estimate';
    }

    return `Send Email for ${localTaxi.description}`;
  });

  const submitBtnText = computed(() => {
    if (data.value.id) {
      return 'Update';
    }

    if (data.value.isWillCall) {
      return 'Create "Will Call" Ride';
    }

    return 'Create';
  });

  const getCommonPostData = (phoneNumber?: string) => {
    let postData = {
      phoneNumber: phoneNumber || data.value.phoneNumber,
      pickupTime: util.formatDateTime(data.value.pickupTime, 'hh:mm A'),
      pickupDate: util.formatDateTime(data.value.pickupDate),
    } as any;

    if (isRepeatedRide.value) {
      postData = {
        ...postData,
        rideMultiple: true,
        repeatEveryXWeek: data.value.repeatEveryXWeek,
        day0: data.value.day0,
        day1: data.value.day1,
        day2: data.value.day2,
        day3: data.value.day3,
        day4: data.value.day4,
        day5: data.value.day5,
        day6: data.value.day6,
        endOn: data.value.endOnType === 'on' ? util.formatDateTime(data.value.endOn, 'YYYY-MM-DD') : null,
        endAfterXTimes: data.value.endOnType === 'after' ? data.value.endAfterXTimes : null,
      };
    } else {
      postData = {
        ...postData,
        rideMultiple: false,
      };
    }

    return postData;
  };

  const getAddressPostData = (startAddress: CustomLocationType, endAddress: CustomLocationType) => {
    trimAddress(startAddress);
    trimAddress(endAddress);
    const startStreetAddress = getStreetAddressByType(startAddress);
    const endStreetAddress = getStreetAddressByType(endAddress);

    const postData: Partial<ScheduledRideInput> = {
      streetAddress1: startStreetAddress,
      city1: startAddress.city,
      state1: startAddress.state,
      zip1: startAddress.zip,
      lat1: Number(startAddress.latitude),
      long1: Number(startAddress.longitude),
      streetAddress2: endStreetAddress,
      city2: endAddress.city,
      state2: endAddress.state,
      zip2: endAddress.zip,
      lat2: Number(endAddress.latitude),
      long2: Number(endAddress.longitude),
      locationName1: startAddress.name,
      locationName2: endAddress.name,
    };

    // Only persist Uber place ids when a Venue Entrance / Side is selected
    if (rideUtility.isVenueEntranceSelected(startAddress)) {
      postData.startLocationId = startAddress.locationId ?? undefined;
      postData.startZoneId = startAddress.zoneId ?? undefined;
      postData.startVenueId = startAddress.venueId ?? undefined;
    }

    return postData;
  };

  const getSchedulePostData = ({
    phoneNumber,
    startAddress,
    endAddress,
    estimationRide,
    partnerId,
  }: {
    phoneNumber?: string;
    startAddress: CustomLocationType;
    endAddress: CustomLocationType;
    estimationRide?: any;
    partnerId?: number | null;
  }): ScheduledRideInput => {
    const commonPostData = getCommonPostData(phoneNumber);
    const addressPostData = getAddressPostData(startAddress, endAddress);

    let postData: ScheduledRideInput = {
      riderPhone: util.getValidPhoneNumber(data.value.riderPhone),
      rideName: data.value.rideName,
      partnerRidePurpose: data.value.partnerRidePurpose,
      rideType: data.value.rideType,
      serviceType: data.value.serviceType,
      timeZone: data.value.timeZone,
      noteToDriver: data.value.noteToDriver,
      isMedicalTrip: data.value.isMedicalTrip,
      partnerId,
      isWillCall: data.value.scheduleKind === 'willcall',
      scheduledAppointmentTime:
        data.value.scheduleKind === 'appointment' ? util.formatDateTime(data.value.scheduledAppointmentTime, 'hh:mm A') : null,
      scheduledAppointmentDate: data.value.scheduleKind === 'appointment' ? util.formatDateTime(data.value.scheduledAppointmentDate) : null,
      scheduledAppointmentBufferMinutes: data.value.scheduleKind === 'appointment' ? data.value.scheduledAppointmentBufferMinutes : null,
      ...commonPostData,
      ...addressPostData,
    };

    if (estimationRide) {
      postData = {
        ...postData,
        rideCostId: estimationRide.rideCostId,
        eta: estimationRide.eta,
        perMile: estimationRide.perMile ? parseFloat(estimationRide.perMile) * 100 : undefined, // multiply with 100 to match RideEstimate records
        perMinute: estimationRide.perMinute ? parseFloat(estimationRide.perMinute) * 100 : undefined, // multiply with 100 to match RideEstimate records
        baseFare: estimationRide.baseFare ? parseFloat(estimationRide.baseFare) * 100 : undefined, // multiply with 100 to match RideEstimate records
        minimumFare: estimationRide.minimumFare ? parseFloat(estimationRide.minimumFare) * 100 : undefined, // multiply with 100 to match RideEstimate records
        operatingFee: estimationRide.operatingFee ? parseFloat(estimationRide.operatingFee) * 100 : 0, // multiply with 100 to match RideEstimate records
        customerCostOverride: estimationRide.customerCostOverride,
        currency: estimationRide.currency,
        surgeFactor: estimationRide.surgeFactor,
        surgeKey: estimationRide.surgeKey,
        surgeUrl: estimationRide.surgeUrl,
        duration: estimationRide.duration,
        distance: estimationRide.distance,
        lowEstimate: estimationRide.lowEstimate,
        highEstimate: estimationRide.highEstimate,
        fareId: estimationRide.fareId,
        pricingExplanation: estimationRide.pricingExplanation,
        storedLongRideOrderDataKey: estimationRide.storedLongRideOrderDataKey,
      };
    }

    return postData;
  };

  const getStawavRideData = ({
    phoneNumber,
    startAddress,
    endAddress,
    partnerId,
  }: {
    phoneNumber?: string;
    startAddress: CustomLocationType;
    endAddress: CustomLocationType;
    partnerId?: number | null;
  }) => {
    const commonPostData = getCommonPostData(phoneNumber);
    const addressPostData = getAddressPostData(startAddress, endAddress);
    const stawavAddressPostData = omit(addressPostData, ['startLocationId', 'startZoneId', 'startVenueId']);

    const payload: StawavScheduledRideInput = {
      gender: data.value.gender,
      levelOfService: data.value.levelOfService,
      hasOwnWheelchair: data.value.hasOwnWheelchair || false,
      specialInstructions: data.value.specialInstructions,
      numberOfCompanions: data.value.numberOfCompanions && Number(data.value.numberOfCompanions),
      appointmentType: data.value.appointmentType,
      appointmentTime: util.formatDateTime(data.value.appointmentTime, 'hh:mm A'),
      returnTime: util.formatDateTime(data.value.returnTime, 'hh:mm A'),
      stairStepsForPU: Number(data.value.stairStepsForPU || 0),
      stairStepsForDO: Number(data.value.stairStepsForDO || 0),
      dropOffPhoneNumber: util.getValidPhoneNumber(data.value.dropOffPhoneNumber),
      partnerId,
      ...commonPostData,
      ...stawavAddressPostData,
    };

    return payload;
  };

  return {
    isLocalTaxiRideType,
    isStawavRideType,
    isGeneralRideType,
    isTransportation,
    isRepeatedRide,
    estimationBtnText,
    submitBtnText,
    isPharmacyOrder,
    isSpecialRequestOrder,
    isFresnoEOCTaxi,
    isGreenPointTaxi,

    getStawavRideData,
    getSchedulePostData,
  };
};
